import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import endpoints from '../../../../../../api/endpoints';
import { useFetch } from '../../../../../../api/useFetch';
import CommonReports from '../../../../../common/reports';
import { downloadReport } from '../../../../../common/reports/utils/downloadReport';
import { HandleDownloadReport } from '../../../../../common/reports/actions/interfaces';
import { getPermissionToGenerateReports } from '../../../../../common/reports/utils/mapReportToTableStructure';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { FiltersFormValues, FiltersInitialData, ReportsData } from '../../../../../common/reports/interfaces';

import locales from './locales';
import FiltersForm from './filters/filtersForm';
import FilterDetails from './filters/filterDetails';
import FiltersSummary from './filters/filtersSummary';
import { mapTableDataIntoApiFormat } from './dataTransformator';
import {
  CustomerAuthenticationReportCreateForm,
  CustomerAuthenticationReportDetailsMetadataResponse
} from './interfaces';
import { filtersValidationSchema } from './filters/filtersValidation';

const initFilters = {
  name: '',
  statuses: [],
  operationIds: [],
  customerMsisdns: [],
  providerNames: [],
  from: null,
  to: null
};

const Reports = () => {
  const { data = {} as ReportsData, loading, refresh, error } = useFetch<ReportsData>({
    pathname: endpoints.customerAuthenticationReports
  });

  const handleDeleteReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerAuthenticationReports}/${id}`, {
      method: 'delete'
    });
  const handleCancelReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerAuthenticationReports}/${id}`, {
      method: 'delete'
    });

  const handleRetryReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerAuthenticationReportsRetry(id)}`, {
      method: 'POST'
    });

  const handleRegenerateReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerAuthenticationReportsRegenerate(id)}`, {
      method: 'POST'
    });

  const handleGenerateReportRequest = (values: CustomerAuthenticationReportCreateForm) =>
    fetchWithTokenRefresh(
      endpoints.customerAuthenticationReports,
      addParams('POST', mapTableDataIntoApiFormat(values))
    );

  const handleDownloadReport: HandleDownloadReport = (id, name, size) => {
    downloadReport(id, `${name}.csv`, 'customerAuthentication', size);
  };

  const location = useLocation<{ filters: FiltersInitialData } | undefined>();

  const [initialValues, setInitialValues] = useState(location.state?.filters || initFilters);

  return (
    <CommonReports
      error={error}
      filters={{
        form: (filtersFormValues: FiltersFormValues<CustomerAuthenticationReportCreateForm>) => (
          <FiltersForm {...filtersFormValues} />
        ),
        validation: filtersValidationSchema,
        details: (metadata: CustomerAuthenticationReportDetailsMetadataResponse) => (
          <FilterDetails filters={metadata} />
        ),
        summary: (metadata: CustomerAuthenticationReportDetailsMetadataResponse) => (
          <FiltersSummary filters={metadata} />
        ),
        initialValues,
        clearValues: () => {
          setInitialValues(initFilters);
        },
        predefinedReportGeneration: Boolean(location.state?.filters)
      }}
      locales={locales}
      data={data}
      emptyMsg="You don't have any Customer Authentication reports to display"
      loading={loading}
      refresh={refresh}
      handleDeleteReportRequest={handleDeleteReportRequest}
      handleCancelReportRequest={handleCancelReportRequest}
      handleRetryReportRequest={handleRetryReportRequest}
      handleRegenerateReportRequest={handleRegenerateReportRequest}
      handleGenerateReportRequest={handleGenerateReportRequest}
      handleDownloadReport={handleDownloadReport}
      generationAllowed={getPermissionToGenerateReports(data.limits?.status)}
    />
  );
};

export default Reports;
